import React, { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';

import { Product } from '@/utils/productHelpers';
import { getProgram } from '@/utils/posthog';
import { getSupabaseClient } from '@/utils/supabaseClient';
import { activateSparkConfirm } from '@/utils/sparkActivationApi';

const EmailAuthCallback: React.FC = () => {
  const history = useHistory();
  const { product } = useParams<{ product: Product }>();
  const posthog = usePostHog();
  const program = getProgram(product);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // TODO: Remove all console.log before merging
        const { data: { session } } = await getSupabaseClient().auth.getSession();
        console.log('[EmailAuthCallback] session exists:', !!session);
        console.log('[EmailAuthCallback] user_id:', session?.user?.id);
        console.log('[EmailAuthCallback] user_metadata:', session?.user?.user_metadata);
        console.log('[EmailAuthCallback] access_token present:', !!session?.access_token);

        if (!session) {
          console.error('[EmailAuthCallback] No session found');
          history.replace(`/activate/${product}/email-auth`);
          return;
        }

        const supabaseUserId = session.user?.id ?? '';
        const patientUid = session.user?.user_metadata?.limbixUserId || '';
        console.log('[EmailAuthCallback] supabaseUserId:', supabaseUserId);
        console.log('[EmailAuthCallback] patientUid (limbixUserId):', patientUid);

        // Flip entitlement to active — same as SmsVerify does after OTP verification.
        try {
          console.log('[EmailAuthCallback] calling activateSparkConfirm...');
          await activateSparkConfirm({ supabaseUserId, accessToken: session.access_token });
          console.log('[EmailAuthCallback] activateSparkConfirm succeeded');
        } catch (err) {
          console.error('[EmailAuthCallback] activateSparkConfirm failed:', err);
        }

        // Check if refreshSession returns status: 'active' after confirm
        const { data: { session: refreshed }, error: refreshError } = await getSupabaseClient().auth.refreshSession();
        console.log('[EmailAuthCallback] refreshSession error:', refreshError);
        console.log('[EmailAuthCallback] refreshed session exists:', !!refreshed);
        console.log('[EmailAuthCallback] refreshed access_token present:', !!refreshed?.access_token);
        console.log('[EmailAuthCallback] refreshed refresh_token present:', !!refreshed?.refresh_token);

        // Decode JWT payload to check bh_claims.status
        if (refreshed?.access_token) {
          try {
            const payload = JSON.parse(atob(refreshed.access_token.split('.')[1]));
            console.log('[EmailAuthCallback] JWT bh_claims:', payload.bh_claims);
            console.log('[EmailAuthCallback] JWT bh_claims.products:', payload.bh_claims?.products);
            console.log('[EmailAuthCallback] entitlement status:', payload.bh_claims?.products?.[0]?.status);
          } catch {
            console.log('[EmailAuthCallback] could not decode JWT');
          }
        }

        posthog?.capture('SparkEmailAuthCallbackSuccess', { program });

        history.replace(`/activate/${product}/user-info/confirmation`, {
          patientUid,
          flow: 'email',
        });
      } catch (err) {
        console.error('[EmailAuthCallback] Unexpected error:', err);
        history.replace(`/activate/${product}/email-auth`);
      }
    };

    handleCallback();
  }, [history, product, posthog, program]);

  return null;
};

export default EmailAuthCallback;
